import React, { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import SplashScreen from "./components/SplashScreen";
import CampaignModal from "./components/CampaignModal";
import Header from "./components/Header";
import CategoryNav from "./components/CategoryNav";
import CategorySection from "./components/CategorySection";
import Footer from "./components/Footer";
import { CATEGORIES } from "./data/mock";

const MenuPage = () => {
  const [showSplash, setShowSplash] = useState(true);
  const [showCampaign, setShowCampaign] = useState(false);
  const [activeCategory, setActiveCategory] = useState(1);
  const observerRef = useRef(null);

  // Handle splash screen completion
  const handleSplashComplete = useCallback(() => {
    setShowSplash(false);
    // Show campaign modal after splash
    setTimeout(() => {
      setShowCampaign(true);
    }, 500);
  }, []);

  // Handle category selection
  const handleCategorySelect = useCallback((categoryId) => {
    setActiveCategory(categoryId);
    const element = document.getElementById(`category-${categoryId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Intersection observer for active category tracking
  useEffect(() => {
    if (showSplash) return;

    const options = {
      root: null,
      rootMargin: '-100px 0px -50% 0px',
      threshold: 0
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const categoryId = parseInt(entry.target.id.replace('category-', ''));
          setActiveCategory(categoryId);
        }
      });
    }, options);

    // Observe all category sections
    CATEGORIES.forEach((category) => {
      const element = document.getElementById(`category-${category.id}`);
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [showSplash]);

  return (
    <div className="min-h-screen bg-neutral-900">
      {/* Splash Screen */}
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}

      {/* Campaign Modal */}
      <CampaignModal open={showCampaign} onClose={() => setShowCampaign(false)} />

      {/* Main Content */}
      <div className={`transition-opacity duration-500 ${showSplash ? 'opacity-0' : 'opacity-100'}`}>
        {/* Header */}
        <Header onCategorySelect={handleCategorySelect} categories={CATEGORIES} />

        {/* Category Navigation */}
        <CategoryNav
          categories={CATEGORIES}
          activeCategory={activeCategory}
          onCategorySelect={handleCategorySelect}
        />

        {/* Menu Content */}
        <main className="max-w-7xl mx-auto px-4 py-8 pt-48">
          <div className="space-y-12">
            {CATEGORIES.map((category) => (
              <CategorySection
                key={category.id}
                category={category}
                isActive={activeCategory === category.id}
              />
            ))}
          </div>
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<MenuPage />} />
          <Route path="*" element={<MenuPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;